import type EddRepository from "@/core/compliance/repositories/EddRepository";
import type { AddEddDocumentInput } from "@/core/compliance/repositories/EddRepository";
import type {
  EddStatus,
  EddRiskAssessment,
  EddCaseSummary,
  EddCaseDetail,
  EddDocument,
  EddHistoryEntry,
  EddMfaChallenge,
} from "@/core/compliance/entities/EddCase";
import { RepositoryResult } from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import {
  executeRequest,
  executeVoidRequest,
} from "../../http/services/executeRequest/executeRequest";
import eddEndpoints from "../../api/eddEndpoints/eddEndpoints";
import {
  type EddCaseSummaryDto,
  type EddCaseDetailDto,
  type EddDocumentDto,
  type EddHistoryEntryDto,
  mapEddCaseSummaryDto,
  mapEddCaseDetailDto,
  mapEddDocumentDto,
  mapEddHistoryDto,
} from "./mappers";

/** Enveloppe paginée renvoyée par le backend pour les listes EDD. */
interface PagedSummariesDto {
  items?: EddCaseSummaryDto[];
  totalElements?: number;
}

export default class EddRepositoryBackend implements EddRepository {
  constructor(private readonly httpClient: HttpClient) {}

  private fetchSummaries(
    url: string,
  ): Promise<RepositoryResult<EddCaseSummary[]>> {
    return executeRequest(
      () => this.httpClient.get<ApiResponse<PagedSummariesDto>>({ url, withAuth: true }),
      (paged) => (paged.items ?? []).map(mapEddCaseSummaryDto),
    );
  }

  getCasesByStatus(status: EddStatus): Promise<RepositoryResult<EddCaseSummary[]>> {
    return this.fetchSummaries(eddEndpoints.casesByStatus(status));
  }

  getAssignedCases(analyst: string): Promise<RepositoryResult<EddCaseSummary[]>> {
    return this.fetchSummaries(eddEndpoints.assigned(analyst));
  }

  getCase(caseId: string): Promise<RepositoryResult<EddCaseDetail>> {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<EddCaseDetailDto>>({
          url: eddEndpoints.case(caseId),
          withAuth: true,
        }),
      mapEddCaseDetailDto,
    );
  }

  getDocuments(caseId: string): Promise<RepositoryResult<EddDocument[]>> {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<EddDocumentDto[]>>({
          url: eddEndpoints.documents(caseId),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapEddDocumentDto),
    );
  }

  getHistory(caseId: string): Promise<RepositoryResult<EddHistoryEntry[]>> {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<EddHistoryEntryDto[]>>({
          url: eddEndpoints.history(caseId),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapEddHistoryDto),
    );
  }

  addDocument(
    caseId: string,
    input: AddEddDocumentInput,
  ): Promise<RepositoryResult<void>> {
    // Backend contract: only `file` is multipart; `type` and `comment` are query params.
    const formData = new FormData();
    formData.append("file", input.file);

    return executeVoidRequest(() =>
      this.httpClient.post<ApiResponse<unknown>, FormData>({
        url: eddEndpoints.addDocument(caseId, input.type, input.comment),
        body: formData,
        withAuth: true,
      }),
    );
  }

  submitL1(caseId: string): Promise<RepositoryResult<void>> {
    return this.putVoid(eddEndpoints.submitL1(caseId), {});
  }
  assign(caseId: string, analyst: string): Promise<RepositoryResult<void>> {
    return this.putVoid(eddEndpoints.assign(caseId), { analyst });
  }
  suspend(caseId: string, reason: string): Promise<RepositoryResult<void>> {
    return this.putVoid(eddEndpoints.suspend(caseId), { reason });
  }
  resume(caseId: string): Promise<RepositoryResult<void>> {
    return this.putVoid(eddEndpoints.resume(caseId), {});
  }
  requestMfaChallenge(caseId: string): Promise<RepositoryResult<EddMfaChallenge>> {
    return executeRequest(
      () =>
        this.httpClient.post<ApiResponse<EddMfaChallenge>, Record<string, never>>({
          url: eddEndpoints.mfaChallenge(caseId),
          body: {},
          withAuth: true,
        }),
      (challenge) => challenge,
    );
  }
  approveL1(caseId: string, comment?: string, otpCode?: string): Promise<RepositoryResult<void>> {
    return this.putVoid(eddEndpoints.approveL1(caseId), {
      ...(comment ? { comment } : {}),
      ...(otpCode ? { otpCode } : {}),
    });
  }
  approveL2(
    caseId: string,
    riskAssessment: EddRiskAssessment,
    conclusion?: string,
    otpCode?: string,
  ): Promise<RepositoryResult<void>> {
    return this.putVoid(eddEndpoints.approveL2(caseId), {
      riskAssessment,
      ...(conclusion ? { conclusion } : {}),
      ...(otpCode ? { otpCode } : {}),
    });
  }
  reject(caseId: string, reason: string, otpCode?: string): Promise<RepositoryResult<void>> {
    return this.putVoid(eddEndpoints.reject(caseId), {
      reason,
      ...(otpCode ? { otpCode } : {}),
    });
  }

  private putVoid(
    url: string,
    body: Record<string, string>,
  ): Promise<RepositoryResult<void>> {
    return executeVoidRequest(() =>
      this.httpClient.put<ApiResponse<void>, Record<string, string>>({
        url,
        body,
        withAuth: true,
      }),
    );
  }
}
